import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { ArrowRightIcon, CheckCircle2Icon, StarIcon, ShieldCheckIcon, ZapIcon, TrophyIcon } from 'lucide-react';
import { SERVICES } from '../utils/servicesData';
const HUB_STATS = [{
  value: '1,000+',
  label: 'Stores delivered'
}, {
  value: '700+',
  label: '5-star reviews'
}, {
  value: '100M+',
  label: 'Client revenue'
}, {
  value: '100%',
  label: 'Shopify focused'
}];
const VALUE_PROPS = [{
  icon: <ShieldCheckIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-1539" />,
  title: 'Shopify specialists',
  desc: 'We do one thing exceptionally well: Shopify. No generalists, no guesswork.'
}, {
  icon: <ZapIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-1540" />,
  title: 'Conversion-obsessed',
  desc: 'Every decision is made to grow your revenue, not just look good.'
}, {
  icon: <TrophyIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-1541" />,
  title: 'Award-recognized',
  desc: 'Top Rated on Fiverr and featured by leading ecommerce platforms.'
}];
export function ServicesPage() {
  usePageSEO({
    canonical: '/services',
    keywords: 'Shopify services, Shopify store development, Shopify theme development, Shopify app development, conversion rate optimization',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Services',
      path: '/services'
    }]),
    title: 'Shopify Services | Design Flow Digitals',
    description: 'Premium Shopify services: store development, custom theme development, app development, and conversion rate optimization. Built by Shopify specialists to grow your revenue.'
  });
  return <div className="w-full" data-id="element-1542">
      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden" data-id="element-1543">
        <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none" data-id="element-1544" />
        <div className="absolute top-0 right-1/4 w-[36rem] h-[36rem] bg-[#D4AF37]/10 rounded-full blur-[140px] animate-pulse-glow pointer-events-none" data-id="element-1545" />

        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10 flex flex-col items-center" data-id="element-1546">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-[#D4AF37]/30" data-id="element-1547">
            <StarIcon className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" data-id="element-1548" />
            <span className="text-sm font-semibold text-[#F3E5AB] tracking-wide uppercase" data-id="element-1549">
              Premium Shopify Services
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight" data-id="element-1550">
            Services that grow your Shopify revenue
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-3xl mb-10" data-id="element-1551">
            End-to-end Shopify solutions, from store builds to custom apps and
            conversion optimization, engineered by specialists for ambitious
            brands.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-5 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-1552">
            Start Your Project <ArrowRightIcon className="w-5 h-5" data-id="element-1553" />
          </Link>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-white/5 bg-[#050505]" data-id="element-1554">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1555">
          {HUB_STATS.map(s => <div key={s.label} className="text-center" data-id="element-1556">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-1557">
                {s.value}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1558">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Service Cards */}
      <section className="section-padding bg-black" data-id="element-1559">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1560">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1561">
            What we do best
          </h2>
          <p className="text-xl text-gray-400" data-id="element-1562">
            Choose a service to see exactly how we deliver results.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-1563">
          {SERVICES.map(service => {
          const Icon = service.icon;
          return <Link key={service.slug} to={`/services/${service.slug}`} className="group glass-card p-10 flex flex-col h-full border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden" data-id="element-1564">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1565" />

                <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300" data-id="element-1566">
                  <Icon className="w-8 h-8 text-[#D4AF37]" data-id="element-1567" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors" data-id="element-1568">
                  {service.title}
                </h3>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1569">
                  {service.heroSubhead}
                </p>

                <ul className="space-y-3 mb-8" data-id="element-1570">
                  {service.deliverables.slice(0, 3).map(d => <li key={d.title} className="flex items-center gap-3 text-gray-300" data-id="element-1571">
                      <CheckCircle2Icon className="w-5 h-5 text-[#D4AF37] shrink-0" data-id="element-1572" />
                      {d.title}
                    </li>)}
                </ul>

                <span className="mt-auto inline-flex items-center gap-2 text-[#D4AF37] font-semibold" data-id="element-1573">
                  Explore {service.title}{' '}
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1574" />
                </span>
              </Link>;
        })}
        </div>
      </section>

      {/* Why Us */}
      <section className="section-padding bg-[#020202] border-y border-white/5" data-id="element-1575">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1576">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1577">
            Why brands trust Design Flow Digitals
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-1578">
          {VALUE_PROPS.map(v => <div key={v.title} className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-colors" data-id="element-1579">
              <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-6" data-id="element-1580">
                {v.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1581">{v.title}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1582">{v.desc}</p>
            </div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-[#D4AF37]/5" data-id="element-1583">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1584">
          Let’s build something exceptional
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1585">
          Tell us about your store and goals, and we’ll show you exactly how
          we’ll help you grow.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-5 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-1586">
          Book a free consultation <ArrowRightIcon className="w-5 h-5" data-id="element-1587" />
        </Link>
      </section>
    </div>;
}